import type { ReactNode } from "react";
import { FieldArrayWithId, FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { CodedInput } from "@/modules/add-new-data/components/coded-input";
import { ScrollableFormShell } from "@/modules/add-new-data/components/scrollable-form-shell";
import { resolveMemberAge } from "@/modules/add-new-data/lib/age";
import { getApplicabilityForQuestion, notApplicableValue, type MemberFieldName } from "@/modules/add-new-data/lib/cleansing";
import { AddNewDataFormValues } from "@/modules/add-new-data/lib/schema";
import { cleanRBIMCode, type RbimCodeQuestionKey } from "@/lib/rbimCodeMap";
import { cn } from "@/lib/utils";

type RbimMigrationSkillsPagesStepProps = {
  errors: FieldErrors<AddNewDataFormValues>;
  fields: FieldArrayWithId<AddNewDataFormValues, "members", "id">[];
  watchedMembers: AddNewDataFormValues["members"];
  register: UseFormRegister<AddNewDataFormValues>;
  setValue: UseFormSetValue<AddNewDataFormValues>;
};

const cellInput =
  "h-8 rounded-none border-0 bg-white px-1.5 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-emerald-700 focus-visible:ring-offset-0";

export function RbimMigrationSkillsPagesStep({
  errors,
  fields,
  watchedMembers,
  register,
  setValue,
}: RbimMigrationSkillsPagesStepProps) {
  return (
    <div className="space-y-5">
      <div className="rounded-xl border bg-white p-4 shadow-sm">
        <p className="text-sm font-semibold text-slate-950">
          Pages 8 - 9: Migration, Community Tax Certificate, and Skills Development
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Continue the RBIM line list for Q38 to Q45.
        </p>
      </div>

      <ScrollableFormShell minWidthClassName="min-w-[3700px]">
        <table className="w-full table-fixed border-collapse text-center text-xs text-slate-950">
          <colgroup>
            <col className="w-[72px]" />
            <col className="w-[300px]" />
            <col className="w-[105px]" />
            <col className="w-[95px]" />
            <col className="w-[160px]" />
            <col className="w-[190px]" />
            <col className="w-[190px]" />
            <col className="w-[190px]" />
            <col className="w-[190px]" />
            <col className="w-[190px]" />
            <col className="w-[190px]" />
            <col className="w-[190px]" />
            <col className="w-[185px]" />
            <col className="w-[175px]" />
            <col className="w-[175px]" />
            <col className="w-[225px]" />
            <col className="w-[225px]" />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={4} className="border border-black bg-[#eeeeee] px-2 py-2 align-bottom font-medium">
                LINE<br />LIST<br />NO.
              </th>
              <th colSpan={3} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                A. Demographic Characteristics
              </th>
              <th colSpan={9} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                E. Migration Information
              </th>
              <th colSpan={2} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                F. Community Tax Certificate
              </th>
              <th colSpan={2} className="border border-black bg-[#eeeeee] px-2 py-2 text-sm font-bold uppercase">
                G. Skills Development
              </th>
            </tr>
            <tr>
              <th colSpan={3} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For All Household Members
              </th>
              <th colSpan={9} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For Migrants and Transients
              </th>
              <th colSpan={2} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For 18 & Above
              </th>
              <th colSpan={2} className="border border-black bg-[#eeeeee] px-2 py-2 font-medium uppercase">
                For 15 & Above
              </th>
            </tr>
            <tr>
              <QuestionHeader code="Q1" title="Name" />
              <QuestionHeader code="Q3" title="Sex" />
              <QuestionHeader code="Q4" title="Age" />
              <QuestionHeader code="Q38" title="Date of Transfer" />
              <QuestionHeader code="Q39A" title="Reason for Leaving Previous Residence" />
              <QuestionHeader code="Q39B" title="Reason for Leaving Previous Residence" />
              <QuestionHeader code="Q39C" title="Reason for Leaving Previous Residence" />
              <QuestionHeader code="Q40" title="Return to Previous Residence" />
              <QuestionHeader code="Q41A" title="Reason for Transferring in this Barangay" />
              <QuestionHeader code="Q41B" title="Reason for Transferring in this Barangay" />
              <QuestionHeader code="Q41C" title="Reason for Transferring in this Barangay" />
              <QuestionHeader code="Q42" title="Duration of Stay in Current Barangay" />
              <QuestionHeader code="Q43A" title="CTC Information" />
              <QuestionHeader code="Q43B" title="Barangay Where CTC Was Issued" />
              <QuestionHeader code="Q44" title="Skills Development Training" />
              <QuestionHeader code="Q45" title="Skills" />
            </tr>
            <tr>
              <Instruction>Copy from previous response</Instruction>
              <Instruction />
              <Instruction />
              <Instruction>When did migrant/transient start residing in this barangay?</Instruction>
              <Instruction colSpan={3}>What are the reason/s why ___ left their previous residence?</Instruction>
              <Instruction>Does ___ plan to return to previous residence? If yes, what year?</Instruction>
              <Instruction colSpan={3}>What are the reason/s why ___ transferred in this barangay?</Instruction>
              <Instruction>Does ___ stay in this barangay? Until when?</Instruction>
              <Instruction>Does ___ have a valid CTC?</Instruction>
              <Instruction>Was the CTC issued in this barangay?</Instruction>
              <Instruction>What skills development training is ___ interested to join in?</Instruction>
              <Instruction>What skill/s do you have?</Instruction>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => {
              const member = watchedMembers[index];
              const memberErrors = errors.members?.[index];
              const age = resolveMemberAge(member ?? {}).ageDisplay || null;
              const name = [member?.lastName, member?.firstName, member?.middleName].filter(Boolean).join(", ");
              const q39AOther = shouldShowDetail(member?.reasonLeavingPreviousResidence1, "q39_reason_leaving_previous_residence", "15");
              const q39BOther = shouldShowDetail(member?.reasonLeavingPreviousResidence2, "q39_reason_leaving_previous_residence", "15");
              const q39COther = shouldShowDetail(member?.reasonLeavingPreviousResidence3, "q39_reason_leaving_previous_residence", "15");
              const q40Detail = shouldShowYesDetail(member?.returnToPreviousResidence, "q40_return_to_previous_residence");
              const q41AOther = shouldShowDetail(member?.transferReason1, "q41_reason_transferring_barangay", "5");
              const q41BOther = shouldShowDetail(member?.transferReason2, "q41_reason_transferring_barangay", "5");
              const q41COther = shouldShowDetail(member?.transferReason3, "q41_reason_transferring_barangay", "5");
              const q42Detail = shouldShowYesDetail(member?.durationOfStayCurrentBarangay, "q42_duration_of_stay_current_barangay");
              const q45Other = shouldShowDetail(member?.skills, "q45_skills", "18");

              return (
                <tr key={field.id}>
                  <LineCell>{index + 1}</LineCell>
                  <ReadOnlyCell>{name || "Copy from Q1"}</ReadOnlyCell>
                  <ReadOnlyCell>{member?.sex || ""}</ReadOnlyCell>
                  <ReadOnlyCell>{age === null ? "" : age}</ReadOnlyCell>
                  <CodeCell
                    questionKey="q38_date_of_transfer"
                    error={memberErrors?.dateOfTransfer?.message}
                    isSkipped={isSkipped(member, "dateOfTransfer")}
                    register={register(`members.${index}.dateOfTransfer`)}
                    value={member?.dateOfTransfer}
                    placeholder="MM/YYYY"
                  />
                  <CodeCell
                    questionKey="q39_reason_leaving_previous_residence"
                    error={memberErrors?.reasonLeavingPreviousResidence1?.message ?? memberErrors?.reasonLeavingPreviousResidence1Other?.message}
                    isSkipped={isSkipped(member, "reasonLeavingPreviousResidence1")}
                    register={register(`members.${index}.reasonLeavingPreviousResidence1`)}
                    detail={q39AOther ? register(`members.${index}.reasonLeavingPreviousResidence1Other`) : undefined}
                    detailError={memberErrors?.reasonLeavingPreviousResidence1Other?.message}
                    value={member?.reasonLeavingPreviousResidence1}
                    placeholder="01"
                  />
                  <CodeCell
                    questionKey="q39_reason_leaving_previous_residence"
                    error={memberErrors?.reasonLeavingPreviousResidence2?.message ?? memberErrors?.reasonLeavingPreviousResidence2Other?.message}
                    isSkipped={isSkipped(member, "reasonLeavingPreviousResidence2")}
                    register={register(`members.${index}.reasonLeavingPreviousResidence2`)}
                    detail={q39BOther ? register(`members.${index}.reasonLeavingPreviousResidence2Other`) : undefined}
                    detailError={memberErrors?.reasonLeavingPreviousResidence2Other?.message}
                    value={member?.reasonLeavingPreviousResidence2}
                    placeholder="02"
                  />
                  <CodeCell
                    questionKey="q39_reason_leaving_previous_residence"
                    error={memberErrors?.reasonLeavingPreviousResidence3?.message ?? memberErrors?.reasonLeavingPreviousResidence3Other?.message}
                    isSkipped={isSkipped(member, "reasonLeavingPreviousResidence3")}
                    register={register(`members.${index}.reasonLeavingPreviousResidence3`)}
                    detail={q39COther ? register(`members.${index}.reasonLeavingPreviousResidence3Other`) : undefined}
                    detailError={memberErrors?.reasonLeavingPreviousResidence3Other?.message}
                    value={member?.reasonLeavingPreviousResidence3}
                    placeholder="03"
                  />
                  <CodeCell
                    questionKey="q40_return_to_previous_residence"
                    error={memberErrors?.returnToPreviousResidence?.message ?? memberErrors?.returnToPreviousResidenceDetail?.message}
                    isSkipped={isSkipped(member, "returnToPreviousResidence")}
                    register={register(`members.${index}.returnToPreviousResidence`)}
                    detail={q40Detail ? register(`members.${index}.returnToPreviousResidenceDetail`) : undefined}
                    detailError={memberErrors?.returnToPreviousResidenceDetail?.message}
                    skippedDetail={!q40Detail && isNoCode(member?.returnToPreviousResidence, "q40_return_to_previous_residence")}
                    value={member?.returnToPreviousResidence}
                    placeholder="1"
                  />
                  <CodeCell
                    questionKey="q41_reason_transferring_barangay"
                    error={memberErrors?.transferReason1?.message ?? memberErrors?.transferReason1Other?.message}
                    isSkipped={isSkipped(member, "transferReason1")}
                    register={register(`members.${index}.transferReason1`)}
                    detail={q41AOther ? register(`members.${index}.transferReason1Other`) : undefined}
                    detailError={memberErrors?.transferReason1Other?.message}
                    value={member?.transferReason1}
                    placeholder="01"
                  />
                  <CodeCell
                    questionKey="q41_reason_transferring_barangay"
                    error={memberErrors?.transferReason2?.message ?? memberErrors?.transferReason2Other?.message}
                    isSkipped={isSkipped(member, "transferReason2")}
                    register={register(`members.${index}.transferReason2`)}
                    detail={q41BOther ? register(`members.${index}.transferReason2Other`) : undefined}
                    detailError={memberErrors?.transferReason2Other?.message}
                    value={member?.transferReason2}
                    placeholder="02"
                  />
                  <CodeCell
                    questionKey="q41_reason_transferring_barangay"
                    error={memberErrors?.transferReason3?.message ?? memberErrors?.transferReason3Other?.message}
                    isSkipped={isSkipped(member, "transferReason3")}
                    register={register(`members.${index}.transferReason3`)}
                    detail={q41COther ? register(`members.${index}.transferReason3Other`) : undefined}
                    detailError={memberErrors?.transferReason3Other?.message}
                    value={member?.transferReason3}
                    placeholder="03"
                  />
                  <CodeCell
                    questionKey="q42_duration_of_stay_current_barangay"
                    error={memberErrors?.durationOfStayCurrentBarangay?.message ?? memberErrors?.durationOfStayCurrentBarangayDetail?.message}
                    isSkipped={isSkipped(member, "durationOfStayCurrentBarangay")}
                    register={register(`members.${index}.durationOfStayCurrentBarangay`)}
                    detail={q42Detail ? register(`members.${index}.durationOfStayCurrentBarangayDetail`) : undefined}
                    detailError={memberErrors?.durationOfStayCurrentBarangayDetail?.message}
                    skippedDetail={!q42Detail && isNoCode(member?.durationOfStayCurrentBarangay, "q42_duration_of_stay_current_barangay")}
                    value={member?.durationOfStayCurrentBarangay}
                    placeholder="1"
                  />
                  <CtcChoiceCell
                    error={memberErrors?.hasValidCtc?.message}
                    isSkipped={isSkipped(member, "hasValidCtc")}
                    questionKey="q43a_has_valid_ctc"
                    register={register(`members.${index}.hasValidCtc`)}
                    value={member?.hasValidCtc}
                  />
                  <CtcChoiceCell
                    error={memberErrors?.ctcIssuedInBarangay?.message}
                    isSkipped={isSkipped(member, "ctcIssuedInBarangay")}
                    questionKey="q43b_ctc_issued_in_barangay"
                    register={register(`members.${index}.ctcIssuedInBarangay`)}
                    value={member?.ctcIssuedInBarangay}
                  />
                  <InputCell
                    error={memberErrors?.skillsTrainingInterest?.message}
                  >
                    {isSkipped(member, "skillsTrainingInterest") ? (
                      <SkippedInput ariaLabel="Q44 not applicable" />
                    ) : (
                      <Input
                        autoUppercase
                        className={cellInput}
                        data-grid-input="true"
                        placeholder="Enter skills development training"
                        {...register(
                          `members.${index}.skillsTrainingInterest`,
                        )}
                      />
                    )}
                  </InputCell>
                  <CodeCell
                    questionKey="q45_skills"
                    error={memberErrors?.skills?.message ?? memberErrors?.skillsOther?.message}
                    isSkipped={isSkipped(member, "skills")}
                    register={register(`members.${index}.skills`)}
                    detail={q45Other ? register(`members.${index}.skillsOther`) : undefined}
                    detailError={memberErrors?.skillsOther?.message}
                    value={member?.skills}
                    placeholder="01"
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollableFormShell>
    </div>
  );
}

function CtcChoiceCell({
  error,
  isSkipped,
  questionKey,
  register,
  value,
}: {
  error?: string;
  isSkipped: boolean;
  questionKey:
    | "q43a_has_valid_ctc"
    | "q43b_ctc_issued_in_barangay";
  register: ReturnType<UseFormRegister<AddNewDataFormValues>>;
  value?: string;
}) {
  return (
    <InputCell error={error}>
      {isSkipped ? (
        <SkippedInput ariaLabel={`${questionKey} not applicable`} />
      ) : (
        <CodedInput
          className={cellInput}
          data-grid-input="true"
          placeholder="Select"
          questionKey={questionKey}
          registration={register}
          value={value}
        />
      )}
    </InputCell>
  );
}
function CodeCell({
  placeholder,
  questionKey,
  error,
  register,
  detail,
  detailError,
  isSkipped = false,
  skippedDetail = false,
  value,
}: {
  placeholder: string;
  questionKey: RbimCodeQuestionKey;
  error?: string;
  register: ReturnType<UseFormRegister<AddNewDataFormValues>>;
  detail?: ReturnType<UseFormRegister<AddNewDataFormValues>>;
  detailError?: string;
  isSkipped?: boolean;
  skippedDetail?: boolean;
  value?: string;
}) {
  return (
    <InputCell error={error}>
      {isSkipped ? (
        <SkippedInput ariaLabel={`${questionKey} not applicable`} />
      ) : (
        <>
          <CodedInput
            className={cellInput}
            data-grid-input="true"
            placeholder={placeholder}
            questionKey={questionKey}
            registration={register}
            value={value}
          />

          {detail ? (
            <Input
              autoUppercase
              className={cn(
                cellInput,
                "border-t border-slate-300",
                detailError && "bg-red-50",
              )}
              data-grid-input="true"
              placeholder={
                questionKey === "q40_return_to_previous_residence"
                  ? "YYYY"
                  : questionKey === "q42_duration_of_stay_current_barangay"
                    ? "Specify details"
                    : "MM/YYYY or detail"
              }
              inputMode={
                questionKey === "q40_return_to_previous_residence"
                  ? "numeric"
                  : undefined
              }
              maxLength={
                questionKey === "q40_return_to_previous_residence"
                  ? 4
                  : undefined
              }
              pattern={
                questionKey === "q40_return_to_previous_residence"
                  ? "[0-9]{4}"
                  : undefined
              }
              {...detail}
            />
          ) : null}

          {skippedDetail ? (
            <SkippedInput
              ariaLabel={`${questionKey} detail not applicable`}
            />
          ) : null}
        </>
      )}
    </InputCell>
  );
}
function QuestionHeader({
  code,
  title,
  children,
  className,
}: {
  code: string;
  title: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <th className={cn("border border-black px-2 py-3 align-middle font-medium uppercase", className)}>
      <span className="block font-bold">{code}</span>
      <span className="block">{title}</span>
      {children && <span className="mt-1 block text-[11px] font-normal normal-case leading-tight">{children}</span>}
    </th>
  );
}

function Instruction({
  children,
  colSpan,
}: {
  children?: ReactNode;
  colSpan?: number;
}) {
  return (
    <th colSpan={colSpan} className="border border-black px-2 py-3 align-top text-[11px] font-normal leading-tight">
      {children}
    </th>
  );
}

function LineCell({ children }: { children: ReactNode }) {
  return <td className="border border-black bg-white px-2 py-1 align-middle font-semibold">{children}</td>;
}

function ReadOnlyCell({ children }: { children: ReactNode }) {
  return <td className="border border-black bg-white px-2 py-1 text-left align-middle text-xs font-medium">{children}</td>;
}

function InputCell({ children, error }: { children: ReactNode; error?: string }) {
  return (
    <td className={cn("border border-black bg-white align-top", error && "bg-red-50 ring-1 ring-inset ring-red-500")}>
      {children}
      {error ? <p className="px-1 pb-1 text-left text-[10px] font-semibold text-red-700">{String(error)}</p> : null}
    </td>
  );
}

function SkippedInput({ ariaLabel }: { ariaLabel: string }) {
  return (
    <Input
      aria-label={ariaLabel}
      className={cn(cellInput, "text-center font-semibold text-slate-600")}
      readOnly
      tabIndex={-1}
      value={notApplicableValue}
    />
  );
}

function isSkipped(member: AddNewDataFormValues["members"][number] | undefined, field: MemberFieldName) {
  return !member || getApplicabilityForQuestion(member, field) === "notApplicable";
}

function shouldShowDetail(
  value: string | undefined,
  questionKey: RbimCodeQuestionKey,
  code: string,
) {
  const result = cleanRBIMCode(questionKey, value);

  return !result.error && result.code === code && !hasInlineDetail(value, code);
}

function shouldShowYesDetail(value: string | undefined, questionKey: RbimCodeQuestionKey) {
  const result = cleanRBIMCode(questionKey, value);

  return !result.error && result.code === "1" && !hasInlineDetail(value, "1");
}

function isNoCode(value: string | undefined, questionKey: RbimCodeQuestionKey) {
  const result = cleanRBIMCode(questionKey, value);

  return !result.error && result.code === "2";
}

function hasInlineDetail(value: string | undefined, code: string) {
  return new RegExp(`^0*${code}(?:\\s*(?:-|:|,)\\s*|\\s+)\\S+`, "i").test(String(value ?? "").trim());
}
